import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  IconClipboardList,
  IconSearch,
  IconLoader2,
  IconX,
  IconUser,
  IconUserOff,
  IconUserPlus,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { api } from "@/lib/api"
import { formatPersonName } from "@/lib/person-identity"
import { toast } from "sonner"
import {
  ResizableColGroup,
  ResizableTableHead,
  getTableWidth,
  usePersistentColumnWidths,
} from "@/components/resizable-table"
import type { ColumnWidthDefinition } from "@/lib/column-widths"
import { queueTotalPages, workflowBadge } from "@/lib/workflow-queue-display"
import { OwnerQueueSummary, useStaffOwnerQueue } from "@/lib/benefit-queue"
import { canReassignBenefitOwnership } from "@/lib/benefit-ownership"

// ── Types ────────────────────────────────────────────────────────────

interface UserDto {
  id: string
  displayName: string
  userPrincipalName: string
}

interface StaffBenefitSummary {
  id: string
  intakeDate: string
  certificationDate: string | null
  benefitType: number
  programName: string
  proofingStatus: number
  statusName: string
  authority: string
  v2State: string | null
  label: string
  readUnavailable: boolean
  amountPaid: number
  grantAmount: number
  applicantLastName: string
  applicantFirstName: string
  ssn: string
  address: string
  intakeLocation: string
  staff: string
  ownership: { userId: string | null; displayName: string | null; email: string | null; version: number } | null
  programYear: number | null
  lastActivity: string | null
}

interface QueuePage {
  items: StaffBenefitSummary[]
  page: number
  pageSize: number
  totalCount: number
}

type UnassignedQueuePage = QueuePage

interface BenefitSearchSummary {
  id: string
  intakeDateTime: string
  certificationDate: string | null
  benefitType: number
  benefitApplicationType?: number | null
  applicantLName: string
  applicantFName: string
  ssn: string
  rAddress: string
  amountPaid: number
  benefitsReceived: string
  status: number
  authority: string
  v2State: string | null
  label: string
  readUnavailable: boolean
  intakeLocation: string
  programYear: number | null
  benefits: string[]
}

// ── Status Helpers ───────────────────────────────────────────────────

function StatusBadge({
  status,
  label,
  authority,
  v2State,
  readUnavailable,
}: {
  status: number
  label?: string
  authority?: string
  v2State?: string | null
  readUnavailable?: boolean
}) {
  const badge = workflowBadge({ authority, v2State, label, readUnavailable }, status)
  return <Badge variant={badge.variant}>{badge.label}</Badge>
}

const programLabels: Record<number, string> = {
  0: "PSE",
  1: "LIHEAP",
  2: "Other",
  3: "Lakehaven",
  4: "Appointment",
  196: "SHEAP",
}

// Year-specific types map to base programs
function getProgramLabel(benefitType: number): string {
  if (programLabels[benefitType]) return programLabels[benefitType]
  // Even year-specific types: odd=PSE, even=LIHEAP for 5+
  if (benefitType >= 5 && benefitType <= 48) {
    return benefitType % 2 === 1 ? "PSE" : "LIHEAP"
  }
  return `Program ${benefitType}`
}

const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DEFAULT_PAGE_SIZE = 50

const STAFF_COLUMNS = [
  { id: "lastName", defaultWidth: 130, minWidth: 90, maxWidth: 260 },
  { id: "firstName", defaultWidth: 130, minWidth: 90, maxWidth: 260 },
  { id: "ssn", defaultWidth: 110, minWidth: 90, maxWidth: 180 },
  { id: "programYear", defaultWidth: 110, minWidth: 90, maxWidth: 160 },
  { id: "certDate", defaultWidth: 110, minWidth: 90, maxWidth: 180 },
  { id: "intakeDate", defaultWidth: 110, minWidth: 90, maxWidth: 180 },
  { id: "location", defaultWidth: 130, minWidth: 90, maxWidth: 260 },
  { id: "status", defaultWidth: 120, minWidth: 95, maxWidth: 220 },
  { id: "program", defaultWidth: 130, minWidth: 90, maxWidth: 240 },
  { id: "amount", defaultWidth: 120, minWidth: 90, maxWidth: 180 },
  { id: "address", defaultWidth: 220, minWidth: 120, maxWidth: 480 },
  { id: "owner", defaultWidth: 180, minWidth: 120, maxWidth: 320 },
  { id: "lastActivity", defaultWidth: 120, minWidth: 95, maxWidth: 180 },
] as const satisfies readonly ColumnWidthDefinition[]

const SEARCH_COLUMNS = [
  { id: "lastName", defaultWidth: 130, minWidth: 90, maxWidth: 260 },
  { id: "firstName", defaultWidth: 130, minWidth: 90, maxWidth: 260 },
  { id: "ssn", defaultWidth: 110, minWidth: 90, maxWidth: 180 },
  { id: "programYear", defaultWidth: 110, minWidth: 90, maxWidth: 160 },
  { id: "certDate", defaultWidth: 110, minWidth: 90, maxWidth: 180 },
  { id: "intakeDate", defaultWidth: 110, minWidth: 90, maxWidth: 180 },
  { id: "location", defaultWidth: 130, minWidth: 90, maxWidth: 260 },
  { id: "status", defaultWidth: 120, minWidth: 95, maxWidth: 220 },
  { id: "type", defaultWidth: 110, minWidth: 85, maxWidth: 220 },
  { id: "benefitsReceived", defaultWidth: 170, minWidth: 120, maxWidth: 360 },
  { id: "address", defaultWidth: 220, minWidth: 120, maxWidth: 480 },
  { id: "amountPaid", defaultWidth: 130, minWidth: 100, maxWidth: 200 },
] as const satisfies readonly ColumnWidthDefinition[]

function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
}) {
  if (totalItems <= pageSize) return null
  const start = (currentPage - 1) * pageSize + 1
  const end = Math.min(currentPage * pageSize, totalItems)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>
          {start}–{end} of {totalItems.toLocaleString()}
        </span>
        <div className="flex items-center gap-1.5">
          <span>Rows:</span>
          {onPageSizeChange ? (
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className="h-7 w-[65px] text-xs" aria-label="Rows per page">
                <SelectValue>{pageSize}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : <span>{pageSize}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="First page"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(1)}
        >
          <IconChevronsLeft className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Previous page"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <IconChevronLeft className="size-4" />
        </Button>
        <span className="px-2 text-sm tabular-nums">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Next page"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <IconChevronRight className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Last page"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          <IconChevronsRight className="size-4" />
        </Button>
      </div>
    </div>
  )
}

// ── Page Component ────────────────────────────────────────

export default function BenefitApplicationsPage() {
  const navigate = useNavigate()

  // User state
  const [users, setUsers] = useState<UserDto[]>([])
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string>("")
  const [canSelectStaff, setCanSelectStaff] = useState(false)
  const [canReviewQueue, setCanReviewQueue] = useState(false)
  const [canViewAllStatuses, setCanViewAllStatuses] = useState(false)
  const [canAssignOwnership, setCanAssignOwnership] = useState(false)
  const [queueView, setQueueView] = useState("intake")
  const [unassignedView, setUnassignedView] = useState("intake")
  const [usersLoading, setUsersLoading] = useState(true)

  // Staff benefits (My Applications) are loaded by the bounded owner-queue hook below.

  // Privileged unassigned queue
  const [unassignedBenefits, setUnassignedBenefits] = useState<StaffBenefitSummary[]>([])
  const [unassignedTotal, setUnassignedTotal] = useState(0)
  const [unassignedPage, setUnassignedPage] = useState(1)
  const [unassignedLoading, setUnassignedLoading] = useState(false)
  const [unassignedError, setUnassignedError] = useState<string | null>(null)

  // Search results
  const [searchResults, setSearchResults] = useState<BenefitSearchSummary[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  // Search fields
  const [lastName, setLastName] = useState("")
  const [firstName, setFirstName] = useState("")
  const [ssn, setSsn] = useState("")
  const [address, setAddress] = useState("")
  const [accountNumber, setAccountNumber] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")

  // Active tab
  const [activeTab, setActiveTab] = useState("my-applications")

  // Pagination
  const [staffPage, setStaffPage] = useState(1)
  const [staffPageSize, setStaffPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchPage, setSearchPage] = useState(1)
  const [searchPageSize, setSearchPageSize] = useState(DEFAULT_PAGE_SIZE)
  const staffColumns = usePersistentColumnWidths(
    "cms.benefitApplications.staffColumns",
    STAFF_COLUMNS,
  )
  const searchColumns = usePersistentColumnWidths(
    "cms.benefitApplications.searchColumns",
    SEARCH_COLUMNS,
  )

  // ── Load users ─────────────────────────────────────────────────────

  useEffect(() => {
    async function loadUsers() {
      try {
        const [allUsers, me, permissions] = await Promise.all([
          api.get<UserDto[]>("/api/users"),
          api.get<UserDto>("/api/users/me"),
          api.get<string[]>("/api/users/me/permissions"),
        ])
        const allStaffPermission = "benefit-applications:view-all-staff"
        setCanSelectStaff(permissions.includes(allStaffPermission))
        setCanAssignOwnership(canReassignBenefitOwnership(permissions))
        const data = permissions.includes("benefit-workflow:data") || permissions.includes("admin:manage-access")
        const proof = permissions.includes("benefit-workflow:proof")
        setCanReviewQueue(data || proof)
        setCanViewAllStatuses(data)
        const defaultView = data ? "all" : proof ? "proofing" : "intake"
        setQueueView(defaultView)
        setUnassignedView(defaultView)
        setUsers(allUsers)
        setCurrentUser(me)
        if (me.id && me.id !== "00000000-0000-0000-0000-000000000000") {
          setSelectedUserId(me.id)
        } else if (allUsers.length > 0 && permissions.includes(allStaffPermission)) {
          setSelectedUserId(allUsers[0]!.id)
        }
      } catch (err) {
        console.error("Failed to load users", err)
      } finally {
        setUsersLoading(false)
      }
    }
    loadUsers()
  }, [])

  // ── Load staff benefits when user/range changes ────────────────────

  const selectedUser = users.find((u) => u.id === selectedUserId)
    ?? (currentUser?.id === selectedUserId ? currentUser : undefined)
  const staffQueue = useStaffOwnerQueue<StaffBenefitSummary>(
    api,
    selectedUserId,
    queueView,
    staffPage,
    staffPageSize,
    activeTab === "my-applications",
  )
  const staffBenefits = staffQueue.items
  const staffTotal = staffQueue.totalCount
  const staffLoading = staffQueue.loading
  const staffError = staffQueue.error
  const fetchStaffBenefits = staffQueue.reload

  const resetStaffQueue = useCallback(() => {
    staffQueue.invalidate()
    setStaffPage(1)
  }, [staffQueue.invalidate])

  const changeStaffPage = useCallback((page: number) => {
    staffQueue.invalidate()
    setStaffPage(page)
  }, [staffQueue.invalidate])

  const changeStaffPageSize = useCallback((size: number) => {
    staffQueue.invalidate()
    setStaffPageSize(size)
    setStaffPage(1)
  }, [staffQueue.invalidate])

  const fetchUnassignedBenefits = useCallback(async () => {
    if (!canAssignOwnership) return
    setUnassignedLoading(true)
    setUnassignedError(null)
    setUnassignedBenefits([])
    setUnassignedTotal(0)
    try {
      const data = await api.get<UnassignedQueuePage>(
        `/api/benefitapplications/unassigned?page=${unassignedPage}&pageSize=${DEFAULT_PAGE_SIZE}&view=${encodeURIComponent(unassignedView)}`,
      )
      setUnassignedBenefits(data.items)
      setUnassignedTotal(data.totalCount)
    } catch (err) {
      setUnassignedError(err instanceof Error ? err.message : "Failed to fetch unassigned queue")
    } finally {
      setUnassignedLoading(false)
    }
  }, [canAssignOwnership, unassignedPage, unassignedView])

  useEffect(() => {
    if (activeTab === "unassigned") fetchUnassignedBenefits()
  }, [activeTab, fetchUnassignedBenefits])

  // ── Search ─────────────────────────────────────────────────────────

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!lastName && !firstName && !ssn && !address && !accountNumber && !phoneNumber) return
    setSearchLoading(true)
    setSearchError(null)
    setSearchResults([])
    setHasSearched(true)
    try {
      const params = new URLSearchParams()
      if (lastName) params.set("LastName", lastName)
      if (firstName) params.set("FirstName", firstName)
      if (ssn) params.set("Ssn", ssn)
      if (address) params.set("Address", address)
      if (accountNumber) params.set("AccountNumber", accountNumber)
      if (phoneNumber) params.set("PhoneNumber", phoneNumber)
      const data = await api.get<BenefitSearchSummary[]>(
        `/api/benefitapplications/summary?${params.toString()}`,
      )
      setSearchResults(data)
      setSearchPage(1)
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : "Search failed")
    } finally {
      setSearchLoading(false)
    }
  }

  const clearSearch = () => {
    setLastName("")
    setFirstName("")
    setSsn("")
    setAddress("")
    setAccountNumber("")
    setPhoneNumber("")
    setSearchResults([])
    setHasSearched(false)
  }

  // Search remains client-paged for its existing cross-staff contract. The selected-owner queue
  // is server-paged and therefore never slices an unbounded response here.
  const paginatedSearch = searchResults.slice(
    (searchPage - 1) * searchPageSize,
    searchPage * searchPageSize,
  )

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="app-page-header">
        <div>
          <div className="flex items-center gap-2">
            <IconClipboardList className="size-6" />
            <h1 className="text-2xl font-bold">Benefit Applications</h1>
          </div>
          <p className="mt-1 text-muted-foreground text-sm">
            Work explicitly assigned files, resolve the privileged unassigned queue, or search across all applications.
          </p>
        </div>
        <Button onClick={() => navigate("/benefit-applications/new")}>
          <IconUserPlus className="size-4" />
          Create New Benefit
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="application-section-tabs">
        <TabsList variant="line" className="application-section-list overflow-x-auto justify-start">
          <TabsTrigger value="my-applications">
            <IconUser className="size-4 mr-1.5" />
            My Applications
          </TabsTrigger>
          {canAssignOwnership && (
            <TabsTrigger value="unassigned">
              <IconUserOff className="size-4 mr-1.5" />
              Unassigned queue
            </TabsTrigger>
          )}
          <TabsTrigger value="search">
            <IconSearch className="size-4 mr-1.5" />
            Search
          </TabsTrigger>
        </TabsList>

        {/* ─── My Applications Tab ────────────────────────────────── */}
        <TabsContent value="my-applications" className="mt-4 space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <IconUser className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">Staff:</span>
            </div>
            {usersLoading ? (
              <Skeleton className="h-9 w-[200px]" />
            ) : canSelectStaff ? (
              <Select value={selectedUserId} onValueChange={(value) => { resetStaffQueue(); setSelectedUserId(value) }}>
                <SelectTrigger className="w-[200px]" aria-label="Staff">
                  <SelectValue placeholder="Select staff...">
                    {selectedUserId ? selectedUser?.displayName || "Selected staff member" : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.displayName}
                      {u.id === currentUser?.id ? " (me)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm text-muted-foreground">
                {currentUser?.displayName ?? "Current staff member"}
              </span>
            )}

            {(canReviewQueue || canViewAllStatuses) && <Select value={queueView} onValueChange={(value) => { resetStaffQueue(); setQueueView(value) }}>
              <SelectTrigger className="w-[220px]" aria-label="Workflow queue view"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="intake">Intake queue</SelectItem>
                {canReviewQueue && <SelectItem value="proofing">Proofer queue</SelectItem>}
                {canViewAllStatuses && <SelectItem value="all">All statuses (history)</SelectItem>}
              </SelectContent>
            </Select>}
            {staffLoading && <IconLoader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>
          <p className="max-w-3xl text-xs text-muted-foreground">
            This is the authenticated staff member&apos;s explicit ownership queue. Files appear here only when
            ownership names this staff member; status history, the legacy Staff label, and opening a file do not
            assign it. Use the privileged Unassigned queue to make deliberate directory-backed assignments, or
            Search to find files across staff.
          </p>

          {/* Summary Cards: total is server-filtered; other metrics are current-page only. */}
          <OwnerQueueSummary items={staffBenefits} totalCount={staffTotal} />

          {/* Staff Benefits Table */}
          {staffError ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8 gap-2">
                <p className="text-sm text-destructive">{staffError}</p>
                <Button variant="outline" size="sm" onClick={fetchStaffBenefits}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Applications — {selectedUser?.displayName ?? "..."}
                </CardTitle>
                <CardDescription>
                  Explicitly owned files for this staff member, oldest intake first. The Owner column is the
                  directory-backed identity used for queue membership; Search remains independent of this queue.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {staffLoading ? (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Skeleton key={i} className="h-8 w-full" />
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table
                        className="table-fixed [&_td]:overflow-hidden [&_td]:text-ellipsis"
                        style={{ width: getTableWidth(STAFF_COLUMNS, staffColumns.widths) }}
                      >
                      <ResizableColGroup definitions={STAFF_COLUMNS} widths={staffColumns.widths} />
                      <TableHeader>
                        <TableRow>
                          {[
                            "Last Name",
                            "First Name",
                            "SSN",
                            "Program Year",
                            "Cert Date",
                            "Intake Date",
                            "Location",
                            "Status",
                            "Program",
                            "Amount",
                            "Address",
                            "Owner",
                            "Last Activity",
                          ].map((label, index) => {
                            const definition = STAFF_COLUMNS[index]!
                            return (
                              <ResizableTableHead
                                key={definition.id}
                                definition={definition}
                                width={staffColumns.widths[definition.id]!}
                                onWidthChange={(width) => staffColumns.resizeColumn(definition.id, width)}
                                label={label}
                                className={definition.id === "amount" ? "text-right" : undefined}
                              >
                                {label}
                              </ResizableTableHead>
                            )
                          })}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {staffBenefits.map((b) => (
                          <TableRow key={b.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/benefit-applications/${b.id}`)}>
                            <TableCell className="font-medium">{b.applicantLastName || "—"}</TableCell>
                            <TableCell>{b.applicantFirstName || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">{b.ssn || "—"}</TableCell>
                            <TableCell className="text-xs tabular-nums">{b.programYear ?? "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {b.certificationDate
                                ? new Date(b.certificationDate).toLocaleDateString()
                                : "—"}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {new Date(b.intakeDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-xs">{b.intakeLocation || "—"}</TableCell>
                            <TableCell>
                              <StatusBadge status={b.proofingStatus} label={b.label} authority={b.authority} v2State={b.v2State} readUnavailable={b.readUnavailable} />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{b.programName}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs tabular-nums">
                              {b.grantAmount > 0
                                ? `$${b.grantAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                : "—"}
                            </TableCell>
                            <TableCell className="text-xs truncate" title={b.address}>
                              {b.address || "—"}
                            </TableCell>
                            <TableCell className="text-xs">
                              <span>{b.ownership?.displayName ?? "Unassigned"}</span>
                              {b.ownership?.email && <span className="block text-muted-foreground truncate" title={b.ownership.email}>{b.ownership.email}</span>}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {b.lastActivity
                                ? new Date(b.lastActivity).toLocaleDateString()
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                        {staffBenefits.length === 0 && !staffLoading && (
                          <TableRow>
                            <TableCell colSpan={13} className="text-center text-muted-foreground py-12">
                              No matching applications found for {selectedUser?.displayName ?? "this user"}.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <Pagination
                    currentPage={staffPage}
                    totalPages={queueTotalPages(staffTotal, staffPageSize)}
                    totalItems={staffTotal}
                    pageSize={staffPageSize}
                    onPageChange={changeStaffPage}
                    onPageSizeChange={changeStaffPageSize}
                  />
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Privileged Unassigned Queue ─────────────────────────── */}
        {canAssignOwnership && (
          <TabsContent value="unassigned" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Unassigned files</CardTitle>
                    <CardDescription>
                      Historical and imported files with no explicit owner. Open a file to choose an active CMS staff identity or leave it deliberately unassigned.
                    </CardDescription>
                  </div>
                  <Select value={unassignedView} onValueChange={(value) => { setUnassignedView(value); setUnassignedPage(1) }}>
                    <SelectTrigger className="w-[210px]" aria-label="Unassigned workflow queue view"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="intake">Intake queue</SelectItem>
                      {canReviewQueue && <SelectItem value="proofing">Proofer queue</SelectItem>}
                      {canViewAllStatuses && <SelectItem value="all">All statuses</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {unassignedError ? (
                  <div className="flex flex-col items-center gap-2 py-8">
                    <p className="text-sm text-destructive">{unassignedError}</p>
                    <Button variant="outline" size="sm" onClick={fetchUnassignedBenefits}>Retry</Button>
                  </div>
                ) : unassignedLoading ? (
                  <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-10 w-full" />)}</div>
                ) : (
                  <>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Applicant</TableHead>
                        <TableHead>Intake date</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Legacy staff label</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {unassignedBenefits.map((application) => (
                          <TableRow key={application.id}>
                            <TableCell>
                              <div className="font-medium">{formatPersonName({ lastName: application.applicantLastName, firstName: application.applicantFirstName })}</div>
                              <div className="font-mono text-xs text-muted-foreground">{application.ssn || "—"}</div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{new Date(application.intakeDate).toLocaleDateString()}</TableCell>
                            <TableCell><Badge variant="outline">{application.programName}</Badge></TableCell>
                            <TableCell><StatusBadge status={application.proofingStatus} label={application.label} authority={application.authority} v2State={application.v2State} readUnavailable={application.readUnavailable} /></TableCell>
                            <TableCell className="text-sm text-muted-foreground">{application.staff || "—"}</TableCell>
                            <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => navigate(`/benefit-applications/${application.id}`)}>Open and assign</Button></TableCell>
                          </TableRow>
                        ))}
                        {unassignedBenefits.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-muted-foreground">No unassigned files in this workflow view.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                    <Pagination
                      currentPage={unassignedPage}
                      totalPages={queueTotalPages(unassignedTotal, DEFAULT_PAGE_SIZE)}
                      totalItems={unassignedTotal}
                      pageSize={DEFAULT_PAGE_SIZE}
                      onPageChange={setUnassignedPage}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Search Tab ─────────────────────────────────────────── */}
        <TabsContent value="search" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Search Benefit Applications</CardTitle>
              <CardDescription>
                Search across all benefit applications by applicant name, SSN, address, account number, or phone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Last Name</label>
                    <Input
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">First Name</label>
                    <Input
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">SSN</label>
                    <Input
                      placeholder="SSN (4 or 9 digits)"
                      value={ssn}
                      onChange={(e) => setSsn(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Address</label>
                    <Input
                      placeholder="Street address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Account #</label>
                    <Input
                      placeholder="Account number"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Phone</label>
                    <Input
                      placeholder="Phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={searchLoading}>
                    {searchLoading ? (
                      <IconLoader2 className="size-4 animate-spin" />
                    ) : (
                      <IconSearch className="size-4" />
                    )}
                    Search
                  </Button>
                  {(lastName || firstName || ssn || address || accountNumber || phoneNumber) && (
                    <Button type="button" variant="ghost" size="sm" onClick={clearSearch}>
                      <IconX className="size-4" />
                      Clear
                    </Button>
                  )}
                  {hasSearched && (
                    <span className="text-sm text-muted-foreground ml-2">
                      {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searchError ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8 gap-2">
                <p className="text-sm text-destructive">{searchError}</p>
              </CardContent>
            </Card>
          ) : hasSearched ? (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  {searchLoading ? (
                    <div className="p-4 space-y-2">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : (
                    <Table
                      className="table-fixed [&_td]:overflow-hidden [&_td]:text-ellipsis"
                      style={{ width: getTableWidth(SEARCH_COLUMNS, searchColumns.widths) }}
                    >
                      <ResizableColGroup definitions={SEARCH_COLUMNS} widths={searchColumns.widths} />
                      <TableHeader>
                        <TableRow>
                          {[
                            "Last Name",
                            "First Name",
                            "SSN",
                            "Program Year",
                            "Cert Date",
                            "Intake Date",
                            "Location",
                            "Status",
                            "Type",
                            "Awarded programs",
                            "Address",
                            "Amount Paid",
                          ].map((label, index) => {
                            const definition = SEARCH_COLUMNS[index]!
                            return (
                              <ResizableTableHead
                                key={definition.id}
                                definition={definition}
                                width={searchColumns.widths[definition.id]!}
                                onWidthChange={(width) => searchColumns.resizeColumn(definition.id, width)}
                                label={label}
                                className={definition.id === "amountPaid" ? "text-right" : undefined}
                              >
                                {label}
                              </ResizableTableHead>
                            )
                          })}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedSearch.map((app) => (
                          <TableRow key={app.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/benefit-applications/${app.id}`)}>
                            <TableCell className="font-medium">{app.applicantLName}</TableCell>
                            <TableCell>{app.applicantFName}</TableCell>
                            <TableCell className="font-mono text-xs">{app.ssn || "—"}</TableCell>
                            <TableCell className="text-xs tabular-nums">{app.programYear ?? "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {app.certificationDate
                                ? new Date(app.certificationDate).toLocaleDateString()
                                : "—"}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {new Date(app.intakeDateTime).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-xs">{app.intakeLocation || "—"}</TableCell>
                            <TableCell>
                              <StatusBadge status={app.status} label={app.label} authority={app.authority} v2State={app.v2State} readUnavailable={app.readUnavailable} />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{app.benefitApplicationType === 100 ? "Combined" : getProgramLabel(app.benefitType)}</Badge>
                            </TableCell>
                            <TableCell className="text-xs truncate" title={app.benefitsReceived}>
                              {app.benefitsReceived || "—"}
                            </TableCell>
                            <TableCell className="text-xs truncate" title={app.rAddress}>
                              {app.rAddress || "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs tabular-nums">
                              {app.amountPaid > 0
                                ? `$${app.amountPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                        {searchResults.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={12} className="text-center text-muted-foreground py-12">
                              No results found. Try different search criteria.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </div>
                <Pagination
                  currentPage={searchPage}
                  totalPages={queueTotalPages(searchResults.length, searchPageSize)}
                  totalItems={searchResults.length}
                  pageSize={searchPageSize}
                  onPageChange={setSearchPage}
                  onPageSizeChange={(s) => (setSearchPageSize(s), setSearchPage(1))}
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
                <IconSearch className="size-8" />
                <p className="text-sm">Enter search criteria above to find benefit applications.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
